'use client';

import { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useAppColors } from '@/styles/colors';
import { getCorporateEventColor, getCorporateEventDisplayLabel } from '@/libs/corporate-event-chart';

type SnapshotRow = {
  competence: string;
  patrimonioLiquido: number | null;
  cotasEmitidas: number | null;
  vpPorCota: number | null;
  source: string | null;
};
type PricePoint = {
  date: string;
  close: number;
};
type PerShareSeriesPoint = {
  date: string;
  price: number | null;
  vpPorCota: number | null;
  pvp: number | null;
};

type TickerPatrimonyTabClientProps = {
  tickerLabel: string;
  snapshots: SnapshotRow[];
  latestPrice: number | null;
  priceHistory: PricePoint[];
  corporateEvents: Array<{
    date: string;
    eventType: string;
    label: string | null;
    splitMultiplier: number | null;
    subscriptionPercentage: number | null;
  }>;
};

type RangeId = '1A' | '3A' | '5A' | '10A' | 'TUDO';

const DAY_MS = 24 * 60 * 60 * 1000;
const PLOT_VERTICAL_PADDING_RATIO = 0.08;

const RANGE_CONFIG: Array<{ id: RangeId; label: string; days: number | null }> = [
  { id: '1A', label: '1A', days: 365 },
  { id: '3A', label: '3A', days: 365 * 3 },
  { id: '5A', label: '5A', days: 365 * 5 },
  { id: '10A', label: '10A', days: 365 * 10 },
  { id: 'TUDO', label: 'TUDO', days: null },
];

const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 2,
});
const BRL_AXIS_NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});
const NUMBER_2_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

export function TickerPatrimonyTabClient({
  tickerLabel,
  snapshots,
  latestPrice,
  priceHistory,
  corporateEvents,
}: TickerPatrimonyTabClientProps) {
  const colors = useAppColors();
  const rangeColors = colors.actions.range;
  const [range, setRange] = useState<RangeId>('5A');
  const orderedSnapshots = useMemo(
    () => [...snapshots].sort((a, b) => new Date(a.competence).getTime() - new Date(b.competence).getTime()),
    [snapshots],
  );

  const availableWindowDays = useMemo(() => {
    if (orderedSnapshots.length < 2) return 0;
    const start = new Date(orderedSnapshots[0].competence).getTime();
    const end = new Date(orderedSnapshots[orderedSnapshots.length - 1].competence).getTime();
    return Math.max(0, Math.floor((end - start) / DAY_MS));
  }, [orderedSnapshots]);

  const filtered = useMemo(() => {
    const ordered = orderedSnapshots;
    if (ordered.length === 0) return ordered;
    const selected = RANGE_CONFIG.find((item) => item.id === range);
    if (!selected || selected.days === null) return ordered;
    const lastTs = new Date(ordered[ordered.length - 1].competence).getTime();
    const threshold = lastTs - selected.days * DAY_MS;
    const next = ordered.filter((item) => new Date(item.competence).getTime() >= threshold);
    return next.length > 1 ? next : ordered;
  }, [orderedSnapshots, range]);

  const latest = useMemo(
    () => [...snapshots].sort((a, b) => new Date(b.competence).getTime() - new Date(a.competence).getTime())[0],
    [snapshots],
  );
  const latestPatrimonio = useMemo(
    () => [...orderedSnapshots].reverse().find((item) => item.patrimonioLiquido !== null)?.patrimonioLiquido ?? null,
    [orderedSnapshots],
  );
  const latestCotas = useMemo(
    () =>
      [...orderedSnapshots].reverse().find((item) => item.cotasEmitidas !== null && item.cotasEmitidas >= 0)
        ?.cotasEmitidas ?? null,
    [orderedSnapshots],
  );
  const latestVpPorCota = useMemo(
    () => [...orderedSnapshots].reverse().find((item) => item.vpPorCota !== null)?.vpPorCota ?? null,
    [orderedSnapshots],
  );
  const latestPvp = useMemo(() => {
    if (latestPrice === null || latestVpPorCota === null || latestVpPorCota <= 0) return null;
    return latestPrice / latestVpPorCota;
  }, [latestPrice, latestVpPorCota]);

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Typography variant='h6' fontWeight={700}>
            Patrimônio de {tickerLabel}
          </Typography>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {RANGE_CONFIG.map((item) => (
              <Button
                key={item.id}
                size='small'
                variant={range === item.id ? 'contained' : 'outlined'}
                disabled={Boolean(item.days && item.days > availableWindowDays)}
                onClick={() => setRange(item.id)}
                sx={
                  range === item.id
                    ? {
                        bgcolor: rangeColors.selectedBg,
                        borderColor: rangeColors.selectedBorder,
                        color: rangeColors.selectedText,
                        '&:hover': {
                          bgcolor: rangeColors.selectedHoverBg,
                          borderColor: rangeColors.selectedHoverBorder,
                        },
                      }
                    : {
                        color: rangeColors.unselectedText,
                        borderColor: rangeColors.unselectedBorder,
                        '&:hover': {
                          color: rangeColors.unselectedHoverText,
                          borderColor: rangeColors.unselectedHoverBorder,
                          backgroundColor: rangeColors.unselectedHoverBg,
                        },
                      }
                }
              >
                {item.label}
              </Button>
            ))}
          </Stack>
        </Stack>

        <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
          Histórico disponível desde {DATE_FORMATTER.format(new Date(orderedSnapshots[0].competence))}
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} sx={{ mt: 2 }}>
          <Metric label='Valor patrimonial atual' value={formatCurrencyOrDash(latestPatrimonio)} />
          <Metric label='Nº de cotas atual' value={formatNumberOrDash(latestCotas)} />
          <Metric label='VP por cota atual' value={formatCurrencyOrDash(latestVpPorCota)} />
          <Metric
            label='Competência'
            value={latest?.competence ? DATE_FORMATTER.format(new Date(latest.competence)) : '-'}
          />
        </Stack>
      </Paper>

      <CombinedPatrimonyChartCard
        snapshots={filtered}
        priceHistory={priceHistory}
        latestPrice={latestPrice}
        latestVpPorCota={latestVpPorCota}
        latestPvp={latestPvp}
        corporateEvents={corporateEvents}
      />
    </Stack>
  );
}

function CombinedPatrimonyChartCard({
  snapshots,
  priceHistory,
  latestPrice,
  latestVpPorCota,
  latestPvp,
  corporateEvents,
}: {
  snapshots: SnapshotRow[];
  priceHistory: PricePoint[];
  latestPrice: number | null;
  latestVpPorCota: number | null;
  latestPvp: number | null;
  corporateEvents: Array<{
    date: string;
    eventType: string;
    label: string | null;
    splitMultiplier: number | null;
    subscriptionPercentage: number | null;
  }>;
}) {
  const colors = useAppColors();
  const whiteColor = colors.semantic.white;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showPatrimonio, setShowPatrimonio] = useState(true);
  const [showCotas, setShowCotas] = useState(false);
  const [showPrice, setShowPrice] = useState(false);
  const [showVp, setShowVp] = useState(false);
  const [showPvp, setShowPvp] = useState(true);
  const series = snapshots.map((item) => ({
    date: item.competence,
    patrimonioLiquido: item.patrimonioLiquido,
    cotasEmitidas: item.cotasEmitidas,
  }));
  const orderedPriceHistory = useMemo(
    () => [...priceHistory].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [priceHistory],
  );
  const priceByMonth = useMemo(() => {
    const monthMap = new Map<string, { ts: number; close: number }>();
    for (const item of orderedPriceHistory) {
      const ts = new Date(item.date).getTime();
      if (!Number.isFinite(ts)) continue;
      const monthKey = item.date.slice(0, 7);
      const current = monthMap.get(monthKey);
      if (!current || ts > current.ts) {
        monthMap.set(monthKey, { ts, close: item.close });
      }
    }
    return monthMap;
  }, [orderedPriceHistory]);
  const perShareSeries = useMemo<PerShareSeriesPoint[]>(() => {
    if (snapshots.length === 0) return [];
    return snapshots.map((snapshot) => {
      const monthKey = snapshot.competence.slice(0, 7);
      const price = priceByMonth.get(monthKey)?.close ?? null;
      const vpPorCota = snapshot.vpPorCota;
      const pvp = price !== null && vpPorCota !== null && vpPorCota > 0 ? price / vpPorCota : null;
      return {
        date: snapshot.competence,
        price,
        vpPorCota,
        pvp,
      };
    });
  }, [snapshots, priceByMonth]);

  const width = 1000;
  const height = 300;
  const chartLeft = 138;
  const chartRight = width - 132;
  const chartTop = 20;
  const chartBottom = height - 42;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;

  const patrimonioSeries = series
    .map((point, index) =>
      point.patrimonioLiquido === null ? null : { index, date: point.date, value: point.patrimonioLiquido },
    )
    .filter((point): point is { index: number; date: string; value: number } => point !== null);

  const cotasSeries = series
    .map((point, index) =>
      point.cotasEmitidas === null || point.cotasEmitidas < 0
        ? null
        : { index, date: point.date, value: point.cotasEmitidas },
    )
    .filter((point): point is { index: number; date: string; value: number } => point !== null);
  const priceSeries = useMemo(() => {
    return perShareSeries
      .map((point) => (point.price === null ? null : { date: point.date, value: point.price }))
      .filter((point): point is { date: string; value: number } => point !== null);
  }, [perShareSeries]);
  const vpSeries = perShareSeries
    .map((point, index) => (point.vpPorCota === null ? null : { index, date: point.date, value: point.vpPorCota }))
    .filter((point): point is { index: number; date: string; value: number } => point !== null);
  const pvpSeries = perShareSeries
    .map((point, index) => (point.pvp === null ? null : { index, date: point.date, value: point.pvp }))
    .filter((point): point is { index: number; date: string; value: number } => point !== null);

  const activePatrimonioSeries = showPatrimonio ? patrimonioSeries : [];
  const activeCotasSeries = showCotas ? cotasSeries : [];
  const activePriceSeries = showPrice ? priceSeries : [];
  const activeVpSeries = showVp ? vpSeries : [];
  const activePvpSeries = showPvp ? pvpSeries : [];
  const filteredCorporateEvents = useMemo(() => {
    if (series.length === 0) return [];
    const startDate = series[0].date.slice(0, 10);
    const endDate = series[series.length - 1].date.slice(0, 10);
    return corporateEvents
      .filter((event) => event.date >= startDate && event.date <= endDate)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [corporateEvents, series]);

  if (
    series.length === 0 ||
    (activePatrimonioSeries.length === 0 &&
      activeCotasSeries.length === 0 &&
      activePriceSeries.length === 0 &&
      activeVpSeries.length === 0 &&
      activePvpSeries.length === 0)
  ) {
    return (
      <Paper variant='outlined' sx={{ p: 2, borderStyle: 'dashed', color: 'text.secondary' }}>
        Histórico combinado: sem dados no período selecionado.
      </Paper>
    );
  }

  const mapChartX = (index: number) =>
    series.length === 1 ? chartLeft + chartWidth / 2 : chartLeft + (index / (series.length - 1)) * chartWidth;
  const mapChartXByDate = (date: string) => {
    const startTs = new Date(series[0].date).getTime();
    const endTs = new Date(series[series.length - 1].date).getTime();
    const ts = new Date(date).getTime();
    if (!Number.isFinite(ts) || endTs <= startTs) return chartLeft;
    const ratio = (ts - startTs) / (endTs - startTs);
    return chartLeft + Math.max(0, Math.min(1, ratio)) * chartWidth;
  };

  const patrimonioRange = getRangeWithPadding(activePatrimonioSeries.map((item) => item.value));
  const cotasRange = getRangeWithPadding(
    activeCotasSeries.map((item) => item.value),
    {
      clampMinToZero: true,
    },
  );
  const priceRange = getRangeWithPadding(
    activePriceSeries.map((item) => item.value),
    {
      clampMinToZero: true,
    },
  );
  const vpRange = getRangeWithPadding(
    activeVpSeries.map((item) => item.value),
    {
      clampMinToZero: true,
    },
  );
  const pvpRange = getRangeWithPadding(
    activePvpSeries.map((item) => item.value),
    {
      clampMinToZero: true,
    },
  );

  const patrimonioPoints = activePatrimonioSeries.map((point) => {
    const x = mapChartX(point.index);
    const y = mapValueToY(point.value, patrimonioRange.min, patrimonioRange.max, chartTop, chartHeight);
    return { x, y, index: point.index };
  });

  const cotasPoints = activeCotasSeries.map((point) => {
    const x = mapChartX(point.index);
    const y = mapValueToY(point.value, cotasRange.min, cotasRange.max, chartTop, chartHeight);
    return { x, y, index: point.index };
  });
  const pricePoints = activePriceSeries.map((point) => {
    const x = mapChartXByDate(point.date);
    const y = mapValueToY(point.value, priceRange.min, priceRange.max, chartTop, chartHeight);
    return { x, y, date: point.date };
  });
  const vpPoints = activeVpSeries.map((point) => {
    const x = mapChartX(point.index);
    const y = mapValueToY(point.value, vpRange.min, vpRange.max, chartTop, chartHeight);
    return { x, y, index: point.index };
  });
  const pvpPoints = activePvpSeries.map((point) => {
    const x = mapChartX(point.index);
    const y = mapValueToY(point.value, pvpRange.min, pvpRange.max, chartTop, chartHeight);
    return { x, y, index: point.index };
  });

  const patrimonioLinePoints = patrimonioPoints.map((point) => `${point.x},${point.y}`).join(' ');
  const cotasLinePoints = cotasPoints.map((point) => `${point.x},${point.y}`).join(' ');
  const priceLinePoints = pricePoints.map((point) => `${point.x},${point.y}`).join(' ');
  const vpLinePoints = vpPoints.map((point) => `${point.x},${point.y}`).join(' ');
  const pvpLinePoints = pvpPoints.map((point) => `${point.x},${point.y}`).join(' ');

  const hoveredPoint =
    hoveredIndex !== null && hoveredIndex >= 0 && hoveredIndex < series.length
      ? {
          x: mapChartX(hoveredIndex),
          data: series[hoveredIndex],
        }
      : null;
  const hoveredPerShare =
    hoveredIndex !== null && hoveredIndex >= 0 && hoveredIndex < perShareSeries.length
      ? perShareSeries[hoveredIndex]
      : null;

  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
      <Typography variant='h6' fontWeight={700}>
        Evolução de patrimônio total, nº de cotas e indicadores por cota
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} sx={{ mt: 1 }}>
        <Metric label='Preço por cota' value={formatCurrencyOrDash(latestPrice)} />
        <Metric label='VP por cota' value={formatCurrencyOrDash(latestVpPorCota)} />
        <Metric label='P/VP' value={formatRatioOrDash(latestPvp)} />
      </Stack>
      <Stack direction='row' spacing={1} sx={{ mt: 0.75 }} flexWrap='wrap' useFlexGap>
        <SeriesToggle
          active={showPatrimonio}
          color={colors.charts.portfolioMix.realEstate}
          whiteColor={whiteColor}
          label='Patrimônio total'
          onClick={() => setShowPatrimonio((value) => !value)}
        />
        <SeriesToggle
          active={showCotas}
          color={colors.charts.portfolioMix.funds}
          whiteColor={whiteColor}
          label='Nº de cotas'
          onClick={() => setShowCotas((value) => !value)}
        />
        <SeriesToggle
          active={showPrice}
          color={colors.charts.portfolioMix.financialAssets}
          whiteColor={whiteColor}
          label='Preço por cota'
          onClick={() => setShowPrice((value) => !value)}
        />
        <SeriesToggle
          active={showVp}
          color={colors.charts.portfolioMix.equities}
          whiteColor={whiteColor}
          label='VP por cota'
          onClick={() => setShowVp((value) => !value)}
        />
        <SeriesToggle
          active={showPvp}
          color={colors.semantic.cyan}
          whiteColor={whiteColor}
          label='P/VP'
          onClick={() => setShowPvp((value) => !value)}
        />
      </Stack>

      <Box sx={{ width: '100%', mt: 1.5 }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width='100%'
          role='img'
          onMouseLeave={() => setHoveredIndex(null)}
          onMouseMove={(event) => {
            if (series.length === 0) return;
            const rect = event.currentTarget.getBoundingClientRect();
            if (rect.width <= 0 || chartWidth <= 0) return;
            const relativeX = ((event.clientX - rect.left) / rect.width) * width;
            const clampedX = Math.max(chartLeft, Math.min(chartRight, relativeX));
            const ratio = (clampedX - chartLeft) / chartWidth;
            const index = Math.round(ratio * (series.length - 1));
            setHoveredIndex(Math.max(0, Math.min(series.length - 1, index)));
          }}
        >
          <rect
            x={chartLeft}
            y={chartTop}
            width={chartWidth}
            height={chartHeight}
            fill='transparent'
            stroke='hsl(var(--border))'
          />

          {showPatrimonio && patrimonioLinePoints ? (
            <polyline
              points={patrimonioLinePoints}
              fill='none'
              stroke={colors.charts.portfolioMix.realEstate}
              strokeWidth='2'
            />
          ) : null}

          {showCotas && cotasLinePoints ? (
            <polyline points={cotasLinePoints} fill='none' stroke={colors.charts.portfolioMix.funds} strokeWidth='2' />
          ) : null}
          {showPrice && priceLinePoints ? (
            <polyline
              points={priceLinePoints}
              fill='none'
              stroke={colors.charts.portfolioMix.financialAssets}
              strokeWidth='2'
              strokeDasharray='5 4'
            />
          ) : null}
          {showVp && vpLinePoints ? (
            <polyline
              points={vpLinePoints}
              fill='none'
              stroke={colors.charts.portfolioMix.equities}
              strokeWidth='2'
              strokeDasharray='5 4'
            />
          ) : null}
          {showPvp && pvpLinePoints ? (
            <polyline
              points={pvpLinePoints}
              fill='none'
              stroke={colors.semantic.cyan}
              strokeWidth='2'
              strokeDasharray='2 3'
            />
          ) : null}
          {filteredCorporateEvents.map((event, index) => {
            const eventTs = new Date(`${event.date}T00:00:00Z`).getTime();
            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;
            for (let indexSeries = 0; indexSeries < series.length; indexSeries += 1) {
              const pointTs = new Date(series[indexSeries].date).getTime();
              const distance = Math.abs(pointTs - eventTs);
              if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = indexSeries;
              }
            }
            const x = mapChartX(closestIndex);
            const color = getCorporateEventColor(event);
            return (
              <g key={`${event.date}-${event.eventType}-${index}`}>
                <line
                  x1={x}
                  y1={chartTop}
                  x2={x}
                  y2={chartBottom}
                  stroke={color}
                  strokeWidth='1.1'
                  strokeDasharray='3 3'
                  opacity='0.7'
                />
                <rect x={x - 2} y={chartTop + 2} width='4' height='8' rx='1' fill={color}>
                  <title>
                    {`${getCorporateEventDisplayLabel(event)} em ${DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))}`}
                  </title>
                </rect>
              </g>
            );
          })}

          {showPatrimonio
            ? patrimonioPoints.map((point) => (
                <circle
                  key={`pl-${point.index}`}
                  cx={point.x}
                  cy={point.y}
                  r='2.2'
                  fill={colors.charts.portfolioMix.realEstate}
                />
              ))
            : null}

          {showCotas
            ? cotasPoints.map((point) => (
                <circle
                  key={`cotas-${point.index}`}
                  cx={point.x}
                  cy={point.y}
                  r='2.2'
                  fill={colors.charts.portfolioMix.funds}
                />
              ))
            : null}

          {hoveredPoint ? (
            <>
              <line
                x1={hoveredPoint.x}
                y1={chartTop}
                x2={hoveredPoint.x}
                y2={chartBottom}
                stroke='hsl(var(--muted-foreground) / 0.6)'
                strokeDasharray='4 4'
              />

              {showPatrimonio && hoveredPoint.data.patrimonioLiquido !== null ? (
                <circle
                  cx={hoveredPoint.x}
                  cy={mapValueToY(
                    hoveredPoint.data.patrimonioLiquido,
                    patrimonioRange.min,
                    patrimonioRange.max,
                    chartTop,
                    chartHeight,
                  )}
                  r='4'
                  fill={colors.charts.portfolioMix.realEstate}
                  stroke={whiteColor}
                  strokeWidth='2'
                />
              ) : null}

              {showCotas && hoveredPoint.data.cotasEmitidas !== null ? (
                <circle
                  cx={hoveredPoint.x}
                  cy={mapValueToY(
                    hoveredPoint.data.cotasEmitidas,
                    cotasRange.min,
                    cotasRange.max,
                    chartTop,
                    chartHeight,
                  )}
                  r='4'
                  fill={colors.charts.portfolioMix.funds}
                  stroke={whiteColor}
                  strokeWidth='2'
                />
              ) : null}

              <g
                transform={`translate(${Math.min(
                  chartRight - 230,
                  Math.max(chartLeft + 8, hoveredPoint.x + 10),
                )}, ${chartTop + 8})`}
              >
                <rect width='220' height='112' rx='8' fill='hsl(var(--card))' stroke='hsl(var(--border))' />
                <text x='10' y='18' fontSize='12' fill='hsl(var(--muted-foreground))'>
                  {DATE_FORMATTER.format(new Date(hoveredPoint.data.date))}
                </text>
                <text
                  x='10'
                  y='36'
                  fontSize='12'
                  fontWeight='700'
                  fill={colors.charts.portfolioMix.realEstate}
                  opacity={showPatrimonio ? 1 : 0.45}
                >
                  Patrimônio: {formatCurrencyOrDash(hoveredPoint.data.patrimonioLiquido)}
                </text>
                <text
                  x='10'
                  y='52'
                  fontSize='12'
                  fontWeight='700'
                  fill={colors.charts.portfolioMix.funds}
                  opacity={showCotas ? 1 : 0.45}
                >
                  Cotas: {formatNumberOrDash(hoveredPoint.data.cotasEmitidas)}
                </text>
                <text x='10' y='68' fontSize='12' fontWeight='700' fill={colors.charts.portfolioMix.financialAssets}>
                  Preço: {formatCurrencyOrDash(hoveredPerShare?.price ?? null)}
                </text>
                <text x='10' y='84' fontSize='12' fontWeight='700' fill={colors.charts.portfolioMix.equities}>
                  VP/cota: {formatCurrencyOrDash(hoveredPerShare?.vpPorCota ?? null)}
                </text>
                <text x='10' y='100' fontSize='12' fontWeight='700' fill={colors.semantic.cyan}>
                  P/VP: {formatRatioOrDash(hoveredPerShare?.pvp ?? null)}
                </text>
              </g>
            </>
          ) : null}

          {showPatrimonio ? (
            <>
              <text
                x={chartLeft - 8}
                y={chartTop + 4}
                textAnchor='end'
                fontSize='12'
                fill='hsl(var(--muted-foreground))'
              >
                {formatCurrencyAxisOrDash(patrimonioRange.max)}
              </text>
              <text
                x={chartLeft - 8}
                y={chartBottom + 4}
                textAnchor='end'
                fontSize='12'
                fill='hsl(var(--muted-foreground))'
              >
                {formatCurrencyAxisOrDash(patrimonioRange.min)}
              </text>
            </>
          ) : null}

          {showCotas ? (
            <>
              <text
                x={chartRight + 8}
                y={chartTop + 4}
                textAnchor='start'
                fontSize='12'
                fill='hsl(var(--muted-foreground))'
              >
                {formatNumberOrDash(cotasRange.max)}
              </text>
              <text
                x={chartRight + 8}
                y={chartBottom + 4}
                textAnchor='start'
                fontSize='12'
                fill='hsl(var(--muted-foreground))'
              >
                {formatNumberOrDash(cotasRange.min)}
              </text>
            </>
          ) : null}
          {showPrice && activePriceSeries.length > 0 ? (
            <>
              <text
                x={chartLeft + 8}
                y={chartTop + 4}
                textAnchor='start'
                fontSize='12'
                fill={colors.charts.portfolioMix.financialAssets}
              >
                {formatCurrencyAxisOrDash(priceRange.max)}
              </text>
              <text
                x={chartLeft + 8}
                y={chartBottom + 4}
                textAnchor='start'
                fontSize='12'
                fill={colors.charts.portfolioMix.financialAssets}
              >
                {formatCurrencyAxisOrDash(priceRange.min)}
              </text>
            </>
          ) : null}
          {showVp && activeVpSeries.length > 0 ? (
            <>
              <text
                x={chartLeft + 92}
                y={chartTop + 4}
                textAnchor='start'
                fontSize='12'
                fill={colors.charts.portfolioMix.equities}
              >
                {formatCurrencyAxisOrDash(vpRange.max)}
              </text>
              <text
                x={chartLeft + 92}
                y={chartBottom + 4}
                textAnchor='start'
                fontSize='12'
                fill={colors.charts.portfolioMix.equities}
              >
                {formatCurrencyAxisOrDash(vpRange.min)}
              </text>
            </>
          ) : null}
          {showPvp && activePvpSeries.length > 0 ? (
            <>
              <text x={chartRight - 8} y={chartTop + 4} textAnchor='end' fontSize='12' fill={colors.semantic.cyan}>
                {formatRatioOrDash(pvpRange.max)}
              </text>
              <text x={chartRight - 8} y={chartBottom + 4} textAnchor='end' fontSize='12' fill={colors.semantic.cyan}>
                {formatRatioOrDash(pvpRange.min)}
              </text>
            </>
          ) : null}

          <text x={chartLeft} y={height - 16} fontSize='12' fill='hsl(var(--muted-foreground))'>
            {DATE_FORMATTER.format(new Date(series[0].date))}
          </text>
          <text x={chartRight} y={height - 16} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
            {DATE_FORMATTER.format(new Date(series[series.length - 1].date))}
          </text>
        </svg>
      </Box>
      {filteredCorporateEvents.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
            Eventos corporativos no período
          </Typography>
          <Stack direction='row' spacing={0.75} useFlexGap flexWrap='wrap' sx={{ mt: 0.5 }}>
            {filteredCorporateEvents.slice(0, 8).map((event, index) => (
              <Stack
                key={`${event.date}-${event.eventType}-${index}`}
                direction='row'
                spacing={0.5}
                alignItems='center'
                sx={{
                  px: 0.75,
                  py: 0.35,
                  borderRadius: 1,
                  backgroundColor: 'hsl(var(--muted) / 0.35)',
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: getCorporateEventColor(event),
                  }}
                />
                <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  {DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))} · {getCorporateEventDisplayLabel(event)}
                </Typography>
              </Stack>
            ))}
            {filteredCorporateEvents.length > 8 ? (
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                +{filteredCorporateEvents.length - 8} eventos
              </Typography>
            ) : null}
          </Stack>
        </Box>
      ) : null}
    </Paper>
  );
}

function getRangeWithPadding(values: number[], options?: { clampMinToZero?: boolean }) {
  if (values.length === 0) return { min: 0, max: 1 };
  let min = values[0];
  let max = values[0];
  for (const value of values) {
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  const range = max - min;
  const padding = range === 0 ? Math.max(Math.abs(max) * 0.08, 1) : range * 0.08;
  const nextMin = options?.clampMinToZero ? Math.max(0, min - padding) : min - padding;
  return { min: nextMin, max: max + padding };
}

function mapValueToY(value: number, min: number, max: number, chartTop: number, chartHeight: number) {
  const innerPadding = chartHeight * PLOT_VERTICAL_PADDING_RATIO;
  const usableHeight = Math.max(1, chartHeight - innerPadding * 2);
  if (min === max) return chartTop + innerPadding + usableHeight / 2;
  const ratio = (value - min) / (max - min);
  return chartTop + innerPadding + usableHeight - ratio * usableHeight;
}

function SeriesToggle({
  active,
  color,
  whiteColor,
  label,
  onClick,
}: {
  active: boolean;
  color: string;
  whiteColor: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      size='small'
      variant={active ? 'contained' : 'outlined'}
      onClick={onClick}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        gap: 0.75,
        ...(active
          ? {
              bgcolor: color,
              borderColor: color,
              '&:hover': { bgcolor: color, opacity: 0.9 },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: active ? whiteColor : color,
        }}
      />
      {label}
    </Button>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='h6' fontWeight={700}>
        {value}
      </Typography>
    </Stack>
  );
}

function formatCurrencyOrDash(value: number | null) {
  if (value === null) return '-';
  return BRL_FORMATTER.format(value);
}

function formatCurrencyAxisOrDash(value: number | null) {
  if (value === null) return '-';
  return `R$ ${BRL_AXIS_NUMBER_FORMATTER.format(value)}`;
}

function formatNumberOrDash(value: number | null) {
  if (value === null) return '-';
  return NUMBER_FORMATTER.format(value);
}

function formatRatioOrDash(value: number | null) {
  if (value === null) return '-';
  return `${NUMBER_2_FORMATTER.format(value)}x`;
}
